/* eslint-disable no-case-declarations */
import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import {
    inject,
    observer
} from 'mobx-react';
import * as React from 'react';
import { RootStore } from '../../stores/root.store';
import { SimpleList, SimpleListItem } from '../../shared/components/simple-list';
import { RunningJobsList } from '../../components/running-jobs-list';
import { JobCreationResult, JobTypes, RunningJobs, STATIC_PROGRESS_LIMIT, StatusTypes } from '../../models/jobs';
import { MainTabs } from '../../stores/manager.store';
import { PageBoundary } from '../../shared/components/simple-list-pagination';
import { CCSnackbarVariant } from '../../shared/components/cc-snackbar';
import { Button, Divider, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { EntityFolder, Reporting } from '../../shared/domain';
import { SimpleModal } from '../../shared/components/simple-modal';

const TASKS_ROWS_PER_PAGE = 10;
const TASKS_PAGING_LIMIT = 100;
const MODAL_CHILDREN_TYPES = {
    year: 'year',
    folderDocId: 'folder_doc_id'
};
const SELECT_VALUES: {[key: string]: [{
    label: string;
    value: string | number | boolean;
}]} = {
    year: [{
        label: '2025-26',
        value: 2025
    }]
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => createStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '2em'
    },
    divider: {
        margin: '1.6em 0 1.55em 0',
    },
    bodyTitle: {
        marginBottom: '0.4em',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        margin: '0 2em'
    },
    errorMessage: {
        width: '100%',
        color: 'red',
        fontWeight: 400,
        textAlign: 'center',
        margin: '1em'
    },
    tasksContainerSize: {
        flex: 1,
        '& .MuiListItem-root': {
            padding: '0.5em !important'
        }
    },
    modal: {
        position: 'absolute',
        padding: '3em'
    },
    modalContainer: {
        padding: '1.5em 0 0',
        width: '100%',
        height: '100%'
    },
    modalField: {
        marginTop: '1.5em',
        '&:first-child': {
            marginTop: '0'
        }
    },
    modalFooter: {
        alignItems: 'center',
        flex: 1,
        padding: '1em',
        display: 'flex',
        justifyContent: 'space-between'
    },
});

interface Props extends WithStyles<typeof styles> {
    rootStore: RootStore;
    tabName?: string;
    availableReports?: Reporting[];
    availableFolders?: EntityFolder[];
}

interface States {
    errorMessage: string;
    runningJobs: RunningJobs[];
    currentJobsOffset: number;
    downloadReportLink: string | undefined;
    snackbarVariant: CCSnackbarVariant;
    snackbarMessage: string;
    snackbarOpen: boolean;
    isOpenCreateReportModal: boolean;
    modalChildrens: React.ReactNode[];
    reportingParameters: {[key: string]: string | number};
    reportingId: string;
    reportingName: string;
    requiredFields: string[];

}

@inject('rootStore')
@observer
class RolesTab extends React.Component<Props, States> {
    public static defaultProps = {
    };
    
    state = {
        errorMessage: '',
        runningJobs: [] as RunningJobs[],
        currentJobsOffset: 0,
        downloadReportLink: undefined,
        snackbarVariant: 'info' as CCSnackbarVariant,
        snackbarMessage: '',
        snackbarOpen: false,
        isOpenCreateReportModal: false,
        modalChildrens: [] as React.ReactNode[],
        reportingParameters: {},
        reportingId: '',
        reportingName: '',
        requiredFields: []
    };

    componentDidMount() {
        this.fetchRunningJobs();
    }

    showSnackbar = (
        snackbarMessage: string,
        snackbarVariant: CCSnackbarVariant
    ) => {
        this.setState({
            snackbarVariant,
            snackbarMessage,
            snackbarOpen: true,
        });
    };

    showError = (errorMessage: string | undefined) => {
        let message;
        if (errorMessage) {
            message = errorMessage;
        } else {
            message = 'Unknown Error';
        }

        this.showSnackbar(message, 'error' as CCSnackbarVariant);
    };

    fetchRunningJobs = () => {
        const {
            rootStore: { managerStore },
        } = this.props;

        const tasks = managerStore.currentStoredJobs.find(e => e.page === MainTabs.Roles);

        if (!tasks) {
            return;
        }

        this.setState({
            runningJobs: tasks.tasks
        }, () => {
            tasks.tasks.forEach(element => {
                if(element.status === 102) {
                    // SET TO RESTART POOLING
                    element.set_to_stop = false;
                    this.startTaskWatcher(element);
                }
            });
        });
    };

    startTaskWatcher = (task: RunningJobs) => {
        const { tabName } = this.props;
        const { runningJobs } = this.state;

        if (!task.task_name || !task.task_id) {
            return;
        }

        const statusCallback = (taskId: string) => {
            const taskIndex = runningJobs.findIndex(e => e.task_id === taskId);
            const taskItem = runningJobs.find(e => e.task_id === taskId);

            if (!taskItem || taskItem.status !== 102) {
                return;
            }

            if (taskItem.set_to_stop) {
                return;
            }

            const {
                rootStore: { managerStore },
            } = this.props;
            
            managerStore.getRunningJobStatus(taskItem.task_id).then(data => {
                const endTime = new Date();
                taskItem.elapsedTime = Math.floor(Math.abs((endTime.getTime() - taskItem.startedAt) / 1000));
                if (data.status === StatusTypes.Error) {
                    taskItem.status = 500;
                    runningJobs[taskIndex] = taskItem;
                }
                if (data.status === StatusTypes.Complete) {
                    taskItem.status = 200;
                    taskItem.progress = 100.0;
                    const { result } = data;
                    if (!result) {
                        this.showError(
                            `Unexpected error occurred reporting - result key is undefined`
                        );
                    } else {
                        const { name } = taskItem.result_data as { name: string };
                        taskItem.task_name = `${name} (${tabName}) - Click to download`;
                        taskItem.result_data = {...taskItem.result_data as object, ...result};
                    }
                } else {
                    // KEEP TRACK OF THE PROGRESS STATUS IF ITS STILL THE SAME
                    if (taskItem.progress === data.completion) {
                        taskItem.static_progress_count += 1;
                    }
                    taskItem.progress = data.completion;
                    // IF WE REACH A POINT WHERE THE STATUS IS THE SAME THE JOB IS SLOW
                    // MAKE FEWER REQUESTS TO NOT OVERWHELM THE STATUS ENDPOINT
                    if (taskItem.static_progress_count === STATIC_PROGRESS_LIMIT) {
                        taskItem.timer += 1000;
                        taskItem.static_progress_count = 0;
                    }
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setTimeout(taskItem.internalCallback!, taskItem.timer, taskId);
                }
                managerStore.setCurrentJobs(runningJobs, MainTabs.Roles);
                this.setState({runningJobs});
            }).catch((error) => {
                const { status } = error.response;
                // CHECK IF WE TIMEOUT OR NOT
                if (status === 408 || status === 504) {
                    // WE HAVE TIMEOUT INCREASE TIMER AND KEEP TRYING
                    taskItem.timer += 1000;
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    setTimeout(taskItem.internalCallback!, taskItem.timer, taskId);
                    return;
                }
                taskItem.status = 500;
                runningJobs[taskIndex] = taskItem;
                managerStore.setCurrentJobs(runningJobs, MainTabs.Roles);
                this.setState({runningJobs});
            });
        };

        // PREVENT GENERATING TIMERS WITHOUT PROPERTIES BEING INITIALIZED
        const taskIndex = runningJobs.findIndex(e => e.task_id === task.task_id);
        runningJobs[taskIndex].internalCallback = statusCallback;
        this.setState({runningJobs}, () => {
            setTimeout(statusCallback, task.timer, task.task_id);
        });
    };

    renderModalChildrens = (childrenType: string, required: boolean, selectValues: {[key: string]: object[]}): React.ReactNode => {
        const { requiredFields } = this.state;

        if (required) {
            this.setState({requiredFields: [...requiredFields, childrenType]});
        }

        switch (childrenType) {
            case MODAL_CHILDREN_TYPES.year:
                const defaultYearOptions = selectValues.year;
                const onChangeYear = (event: SelectChangeEvent<string>) => {
                    const { reportingParameters, requiredFields } = this.state;
                    const year = event.target.value;
                    this.setState({
                        reportingParameters: {...reportingParameters, year},
                        requiredFields: requiredFields.filter(item => item !== MODAL_CHILDREN_TYPES.year)
                    });
                };

                return (
                    <FormControl fullWidth required={required}>
                        <InputLabel id="select-year">Select Year</InputLabel>
                        <Select
                            labelId="select-year"
                            id="select-year"
                            label="Select Year"
                            onChange={onChangeYear}
                            defaultValue="2025"
                        >
                            {
                                defaultYearOptions.map((item, index) => {
                                    const { label, value } = item as { label: string, value: number };
                                    return (
                                        <MenuItem key={index} value={value}>{label}</MenuItem>
                                    );
                                })
                            }
                        </Select>
                    </FormControl>
                );
            case MODAL_CHILDREN_TYPES.folderDocId:
                const { availableFolders } = this.props;
                const options = !availableFolders ? [] : availableFolders;
                const onChangeFolder = (event: SelectChangeEvent<string>) => {
                    const { reportingParameters, requiredFields } = this.state;
                    const folderId = event.target.value;
                    this.setState({
                        reportingParameters: {...reportingParameters, folder_doc_id: folderId},
                        requiredFields: requiredFields.filter(item => item !== MODAL_CHILDREN_TYPES.folderDocId)
                    });
                };

                return (
                    <FormControl fullWidth required={required}>
                        <InputLabel id="select-folder">Select School</InputLabel>
                        <Select
                            labelId="select-folder"
                            id="select-folder"
                            label="Select Folder"
                            onChange={onChangeFolder}
                            defaultValue=""
                        >
                            {
                                options.map((folder: EntityFolder) => {
                                    const { folder_id, folder_name} = folder;
                                    return <MenuItem key={folder_id} value={folder_id}>{folder_name}</MenuItem>;
                                })
                            }
                        </Select>
                    </FormControl>
                );
            default:
                return '' as React.ReactNode;
        }
    };

    onDownloadLinkReport = (item: SimpleListItem) => {
        const {
            id,
            label,
            data
        } = item;

        const {
            parameters: parametersData,
            selectValues 
        } = data as {
            parameters: {[key: string]: object}
            selectValues: {[key: string]: object[]}
        };

        const parametersKeys = !parametersData ? [] : Object.keys(parametersData);

        // Check if parameters are empty to directly create the report
        if (!parametersData || !parametersKeys.length) {
            this.createReport(id, label);
            return;
        }

        // Open modal to fill the parameters to create the report
        const modalChildrens = parametersKeys.map((childrenType) => {
            const { required } = parametersData[childrenType] as { required: boolean };
            return this.renderModalChildrens(childrenType, required, selectValues);
        });

        this.setState({
            isOpenCreateReportModal: true,
            modalChildrens,
            reportingId: id,
            reportingName: label
        });
    };

    createReport = (reportingId: string, reportingName: string, parameters?: object) => {
        const {
            rootStore: { managerStore },
            tabName
        } = this.props;
        const { runningJobs } = this.state;

        managerStore.createSecurityReportLink(reportingId, parameters).then((response: JobCreationResult) => {
            const { jobId } = response;
            const newJob: RunningJobs = {
                task_name: `Create reporting: ${reportingName} (${tabName})`,
                task_id: jobId,
                status: 102,
                type: JobTypes.ReportCreation,
                startedAt: Date.now(),
                elapsedTime: 0,
                progress: 0,
                timer: 1500,
                static_progress_count: 0,
                set_to_stop: false,
                result_data: {
                    name: reportingName
                }
            };

            runningJobs.push(newJob);
            managerStore.setCurrentJobs(runningJobs, MainTabs.Roles);
            this.startTaskWatcher(newJob);
            this.setState({ runningJobs });
        }).catch((error) => {
            this.showError(`ERROR: ${error}`);
        });
    };

    onCreateReportModal = () => {
        const {
            reportingId,
            reportingName,
            reportingParameters
        } = this.state;

        this.createReport(reportingId, reportingName, reportingParameters);
        this.closeReportModal();
    };

    onJobsDataPageOverBoundaryReached = (boundary: PageBoundary, nextPage: number): Promise<void> => {
        return new Promise<void>(() => {
            const { 
                currentJobsOffset,
                runningJobs
            } = this.state;
            const totalTasks = runningJobs.length;
            const isFirstPage = nextPage === 0;
            const isLastPage = nextPage === (Math.ceil(totalTasks / TASKS_ROWS_PER_PAGE)-1);
            const newOffset = isFirstPage ? 0
                : isLastPage ? TASKS_PAGING_LIMIT * Math.floor(totalTasks / TASKS_PAGING_LIMIT)
                    : (boundary === PageBoundary.Upper) ? 
                        currentJobsOffset+TASKS_PAGING_LIMIT :
                        currentJobsOffset-TASKS_PAGING_LIMIT;
            const nextTasks = runningJobs;
            // MANUAL OFFSET
            this.setState({
                runningJobs: nextTasks.splice(newOffset, nextTasks.length)
            });
        });
    };

    onJobClick = (job: RunningJobs) => {
        if (job.status === 102) {
            return;
        }

        if (job.status === 200) {
            const reportUrl = job.result_data as { url: string };
            window.location.href = reportUrl.url;
            return;
        }

        const {
            rootStore: { managerStore }
        } = this.props;
        managerStore.getRunningJobError(job.task_id)
            .then((response) => {
                this.showError(`An error occurred reporting for ${job.task_name} - ${response.error}`);
            }).catch((error) => {
                this.showError(
                    `Unexpected error occurred reporting - ${error}`
                );

            });
    };

    onJobClear = () => {
        const { runningJobs } = this.state;
        const pendingTasks = runningJobs.filter(e => e.status === 102);
        const {
            rootStore: { managerStore }
        } = this.props;

        managerStore.setCurrentJobs(pendingTasks, MainTabs.Roles);
        this.setState({ runningJobs: pendingTasks });
    };

    closeReportModal = () => {
        this.setState({
            isOpenCreateReportModal: false,
            reportingName: '',
            reportingParameters: {},
            requiredFields: []
        });
    };

    public render() {
        const {
            availableReports,
            classes,
            rootStore: {
                managerStore: { isRolesTabLoading }
            }
        } = this.props;
        const {
            runningJobs,
            currentJobsOffset,
            isOpenCreateReportModal,
            modalChildrens,
            reportingName,
            requiredFields
        } = this.state;

        if (!availableReports) {
            return (
                <div
                    className={classes.errorContainer}
                >
                    <Typography
                        className={classes.errorMessage}
                        variant="h2"
                    >
                        There is no available reports to create
                    </Typography>
                    <Typography
                        variant="h4"
                    >
                        Please reload the page.
                    </Typography>
                    <Typography
                        variant="h4"
                    >
                        If the problem persists, please contact support at sayhello@crowdcomfort.com
                    </Typography>
                </div>
            );
        }

        const items = availableReports.map((report: Reporting) => {
            const {
                reporting_id,
                reporting_name,
                parameters,
                select_values
            } = report;

            const data: {
                [key: string]: string | object;
            } = {
                parameters,
                selectValues: !select_values ? SELECT_VALUES : select_values
            };

            return new SimpleListItem(
                reporting_id as string,
                reporting_name,
                `Click to create report`,
                undefined,
                undefined,
                undefined,
                data
            );
        });

        return (
            <div className={classes.root}>
                <Typography
                    className={classes.bodyTitle}
                    variant="h4"
                >
                    Available Reports
                </Typography>
                <SimpleList
                    isLoading={isRolesTabLoading}
                    items={items}
                    keepItemSelected={isRolesTabLoading}
                    onListItemClick={this.onDownloadLinkReport}
                />
                <Divider className={classes.divider} />
                {
                    (runningJobs && (runningJobs.length > 0)) &&
                        <>
                            <Typography
                                className={classes.bodyTitle}
                                variant="h4"
                            >
                                Created Reports
                            </Typography>
                            <RunningJobsList
                                className={classes.tasksContainerSize}
                                tasks={runningJobs}
                                rowsPerPage={TASKS_ROWS_PER_PAGE}
                                taskItemsOffset={currentJobsOffset}
                                onJobsDataPageOverBoundary={this.onJobsDataPageOverBoundaryReached}
                                onJobClick={this.onJobClick}
                                onJobClear={this.onJobClear}
                            />
                        </>
                }
                <SimpleModal
                    className={classes.modal}
                    open={isOpenCreateReportModal}
                    onModalResult={this.onCreateReportModal}
                    buttonOkLabel=""
                    buttonCancelLabel=""
                    footer={
                        <div className={classes.modalFooter}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={this.closeReportModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={this.onCreateReportModal}
                                disabled={requiredFields.length !== 0 ? true : false}
                            >
                                Create
                            </Button>
                        </div>
                    }
                >
                    <>
                        <Typography
                            variant="h5"
                        >
                            Create Report - {reportingName}
                        </Typography>
                        <div className={classes.modalContainer}>
                            {
                                modalChildrens.map((children, index) => {
                                    return <div key={index} className={classes.modalField}>
                                        {children}
                                    </div>;
                                })
                            }
                        </div>
                    </>
                </SimpleModal>
            </div>
        );
    }
}

const MUIComponent = withStyles(styles)(RolesTab);
export { MUIComponent as RolesTab };
